import { useEffect, useState } from "react";
import StatCard from "../components/StatCard";
import CustomBarChart from "../components/CustomBarChart";
import SplashScreen from "../components/SplashScreen";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import MapView from "../components/MapView";
import MapDash from "../components/MapDash";
import { useTranslation } from "react-i18next";

const Biodiversity = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [filter, setFilter] = useState("all");
  const [mapData, setMapData] = useState([]);

  useEffect(() => {
    const storedData = localStorage.getItem("biodiversityFormData");
    setMapData(storedData ? JSON.parse(storedData) : []);
    const parsedArray = storedData ? JSON.parse(storedData) : [];

    // دالة تجمع قيمة مفتاح معين عبر كل العناصر
    const sumKey = (arr, key) =>
      arr.reduce((sum, item) => sum + Number(item[key] || 0), 0);

    const transformed = {
      tourism_boats_daily: { siteA: sumKey(parsedArray, "dailyTouristBoats") },
      operational_moorings: { siteA: sumKey(parsedArray, "mooringPoints") },
      visitors_per_site: { siteA: sumKey(parsedArray, "visitorsPerSite") },

      coral_change_percent: sumKey(parsedArray, "coralChange"),
      trained_crew_percent: sumKey(parsedArray, "trainedCrew"),
      trained_guides_percent: sumKey(parsedArray, "trainedGuides"),
      eco_friendly_sports_percent: sumKey(parsedArray, "ecoWaterSports"),
      sustainability_incentives: sumKey(
        parsedArray,
        "sustainabilityIncentives"
      ),
      green_fins_growth_percent: sumKey(parsedArray, "greenFinsMembers"),
      green_fins_operator_incentives: sumKey(
        parsedArray,
        "greenFinsIncentives"
      ),
      reef_species_change_percent: sumKey(parsedArray, "reefSpeciesChange"),
      pa_trained_personnel_percent: sumKey(parsedArray, "paTrainedPersonnel"),
      pa_budget_growth_percent: sumKey(parsedArray, "paBudgetIncrease"),
      enforcement_patrols: sumKey(parsedArray, "enforcementPatrols"),
      enforcement_actions: sumKey(parsedArray, "patrolActions"),
      boats_with_holding_tanks: sumKey(parsedArray, "boatsWithSewageTanks"),
      nutrient_level_reduction_percent: sumKey(
        parsedArray,
        "seaNutrientDecrease"
      ),
      pump_out_facility_boats: sumKey(parsedArray, "boatsUsingLandFacilities"),
      effluent_quality_improvement_percent: sumKey(
        parsedArray,
        "dischargeSalinityDecrease"
      ),
      grey_water_reduction_percent: sumKey(
        parsedArray,
        "greyWaterUseReduction"
      ),
      reef_safe_product_operators: sumKey(
        parsedArray,
        "operatorsPromotingSafeProducts"
      ),
      marine_cleanups: sumKey(parsedArray, "marineCleanups"),
      coastal_damage_reports: sumKey(parsedArray, "coastalDamageReports"),
      illegal_fishing_reports: sumKey(parsedArray, "illegalFishingReports"),
      bird_mortality_decrease_percent: sumKey(
        parsedArray,
        "birdMortalityDecrease"
      ),
      bird_rehab_actions: sumKey(parsedArray, "birdRehabilitationCases"),
      protected_turtle_sites: sumKey(parsedArray, "turtleNestingSites"),
      rescued_turtles: sumKey(parsedArray, "turtlesRescued"),
    };

    setData(transformed);
    setTimeout(() => setShowSplash(false), 200);
  }, []);

  if (showSplash || !data) return <SplashScreen />;

  const boatsData = Object.entries(data.tourism_boats_daily).map(
    ([site, value]) => ({ site, value })
  );
  const mooringsData = Object.entries(data.operational_moorings).map(
    ([site, value]) => ({ site, value })
  );
  const visitorsData = Object.entries(data.visitors_per_site).map(
    ([site, value]) => ({ site, value })
  );
  console.log(data);

  return (
    <>
      <Helmet>
        <title>{t("biodiversity")} | {t("dashboard")}</title>
      </Helmet>

      <div className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden">
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("biodiversity")}
          </h1>
          {/* أزرار الفلترة */}
          <div className="flex flex-col sm:flex-row gap-2 text-start w-full justify-between items-center" >
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
            >
              <option value="all">{t("all")}</option>
              <option value="tourism">{t("tourism_sites")}</option>
              <option value="training">{t("training_environmental_practices")}</option>
              <option value="reef">{t("reef_marine_impact")}</option>
              <option value="conservation">{t("conservation_actions")}</option>
            </select>

            {/* زر التعديل */}
            <button
              onClick={() => navigate("/BiodiversityForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* باقي الصفحة - الكروت */}
          <div
            className="col-span-1 space-y-6 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 flex flex-col gap-4"
            dir="ltr"
          >
            {/* المواقع السياحية */}
            {(filter === "all" || filter === "tourism") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">{t("tourism_sites")}</h2>
                <div className="grid grid-cols-1 gap-4">
                  <CustomBarChart
                    data={boatsData}
                    xKey="site"
                    barKey="value"
                    title={t("daily_tourist_boats")}
                    barColor="#3b82f6"
                  />
                  <CustomBarChart
                    data={mooringsData}
                    xKey="site"
                    barKey="value"
                    title={t("operational_moorings")}
                    barColor="#22c55e"
                  />
                  <CustomBarChart
                    data={visitorsData}
                    xKey="site"
                    barKey="value"
                    title={t("visitors_per_site")}
                    barColor="#facc15"
                  />
                </div>
              </div>
            )}
            {/* التدريب والممارسات البيئية */}
            {(filter === "all" || filter === "training") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">
                  {t("training_environmental_practices")}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-3">
                  <StatCard
                    title={t("coral_reef_change")}
                    value={data.coral_change_percent + "%"}
                  />
                  <StatCard
                    title={t("trained_crew")}
                    value={data.trained_crew_percent + "%"}
                  />
                  <StatCard
                    title={t("trained_guides")}
                    value={data.trained_guides_percent + "%"}
                  />
                  <StatCard
                    title={t("eco_friendly_water_sports")}
                    value={data.eco_friendly_sports_percent + "%"}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <StatCard
                    title={t("sustainability_incentives")}
                    value={data.sustainability_incentives}
                  />
                  <StatCard
                    title={t("green_fins_membership_growth")}
                    value={data.green_fins_growth_percent + "%"}
                  />
                  <StatCard
                    title={t("green_fins_operator_incentives")}
                    value={data.green_fins_operator_incentives}
                  />
                </div>
              </div>
            )}
            {/* تأثير الشعاب والبيئة البحرية */}
            {(filter === "all" || filter === "reef") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">
                  {t("reef_marine_impact")}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-3">
                  <StatCard
                    title={t("reef_species_change")}
                    value={data.reef_species_change_percent + "%"}
                  />
                  <StatCard
                    title={t("pa_trained_personnel")}
                    value={data.pa_trained_personnel_percent + "%"}
                  />
                  <StatCard
                    title={t("pa_budget_growth")}
                    value={data.pa_budget_growth_percent + "%"}
                  />
                  <StatCard
                    title={t("enforcement_patrols")}
                    value={data.enforcement_patrols}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 mb-3">
                  <StatCard
                    title={t("boats_holding_tanks")}
                    value={data.boats_with_holding_tanks}
                  />
                  <StatCard
                    title={t("nutrient_reduction")}
                    value={data.nutrient_level_reduction_percent + "%"}
                  />
                  <StatCard
                    title={t("boats_pump_out_facilities")}
                    value={data.pump_out_facility_boats}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <StatCard
                    title={t("effluent_quality_improvement")}
                    value={data.effluent_quality_improvement_percent + "%"}
                  />
                  <StatCard
                    title={t("grey_water_reduction")}
                    value={data.grey_water_reduction_percent + "%"}
                  />
                  <StatCard
                    title={t("reef_safe_product_operators")}
                    value={data.reef_safe_product_operators}
                  />
                </div>
              </div>
            )}
            {/* إجراءات الحماية */}
            {(filter === "all" || filter === "conservation") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3 text-start">{t("conservation_actions")}</h2>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <StatCard
                    title={t("marine_cleanups")}
                    value={data.marine_cleanups}
                  />
                  <StatCard
                    title={t("coastal_damage_reports")}
                    value={data.coastal_damage_reports}
                  />
                  <StatCard
                    title={t("illegal_fishing_reports")}
                    value={data.illegal_fishing_reports}
                  />
                  <StatCard
                    title={t("enforcement_actions")}
                    value={data.enforcement_actions}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    title={t("bird_mortality_decrease")}
                    value={data.bird_mortality_decrease_percent + "%"}
                  />
                  <StatCard
                    title={t("bird_rehab_actions")}
                    value={data.bird_rehab_actions}
                  />
                  <StatCard
                    title={t("protected_turtle_sites")}
                    value={data.protected_turtle_sites}
                  />
                  <StatCard
                    title={t("rescued_turtles")}
                    value={data.rescued_turtles}
                  />
                </div>
              </div>
            )}
          </div>

          {/* العمود الخاص بالخريطة - 2/3 */}
          <div className="md:col-span-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20">
            <MapDash initialData={mapData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Biodiversity;


